/**
 * categoryMockData — Datos mock de categorías y top productos por periodo
 *
 * QUÉ HACE: Provee datos mock de distribución por categoría y top productos
 *   para cada periodo (hoy, semana, mes, año) del dashboard de métricas.
 * QUÉ EXPORTA: CATEGORY_PERIOD_DATA, DonutPeriod
 * QUÉ NO DEBE HACER: NO contener lógica de UI ni componentes.
 *   NO hacer llamadas a servicios reales.
 * REGLA: Los valores de categorías deben ser consistentes con los top productos
 *   del mismo periodo (la suma de unidades de productos de una categoría = value del slice).
 * CUANDO SE CONECTE EL BACKEND: reemplazar este archivo por llamadas al servicio
 *   sin modificar ni DonutChart ni MetricsPage.
 */

import type { ChartSlice } from "@/components/ui";
import type { TopProduct } from "../types/metrics.types";

export type DonutPeriod = "hoy" | "semana" | "mes" | "anio";

interface PeriodData {
  /** Segmentos del donut (consistentes con topProducts del mismo periodo) */
  categories: ChartSlice[];
  /** Top 5 productos del periodo */
  topProducts: TopProduct[];
}

export const CATEGORY_PERIOD_DATA: Record<DonutPeriod, PeriodData> = {
  hoy: {
    categories: [
      { name: "Bebidas", value: 20, color: "#3b82f6" },
      { name: "Panadería", value: 15, color: "#10B981" },
      { name: "Snacks", value: 8, color: "#8B5CF6" },
      { name: "Postres", value: 4, color: "#F59E0B" },
      { name: "Otros", value: 3, color: "#94A3B8" },
    ],
    topProducts: [
      {
        id: "prod_1",
        nombre: "Café Premium",
        cantidadVendida: 12,
        totalIngresos: 90000,
        categoria: "Bebidas",
      },
      {
        id: "prod_2",
        nombre: "Pan Artesanal",
        cantidadVendida: 9,
        totalIngresos: 45000,
        categoria: "Panadería",
      },
      {
        id: "prod_3",
        nombre: "Jugo Natural",
        cantidadVendida: 8,
        totalIngresos: 40000,
        categoria: "Bebidas",
      },
      {
        id: "prod_4",
        nombre: "Croissant",
        cantidadVendida: 6,
        totalIngresos: 30000,
        categoria: "Panadería",
      },
      {
        id: "prod_5",
        nombre: "Torta de Chocolate",
        cantidadVendida: 4,
        totalIngresos: 60000,
        categoria: "Postres",
      },
      {
        id: "prod_6",
        nombre: "Agua Mineral",
        cantidadVendida: 4,
        totalIngresos: 8000,
        categoria: "Bebidas",
      },
      {
        id: "prod_7",
        nombre: "Chips de Maíz",
        cantidadVendida: 3,
        totalIngresos: 10500,
        categoria: "Snacks",
      },
      {
        id: "prod_8",
        nombre: "Granola Bar",
        cantidadVendida: 2,
        totalIngresos: 12000,
        categoria: "Snacks",
      },
      {
        id: "prod_9",
        nombre: "Smoothie de Frutas",
        cantidadVendida: 2,
        totalIngresos: 18000,
        categoria: "Bebidas",
      },
      {
        id: "prod_10",
        nombre: "Baguette",
        cantidadVendida: 2,
        totalIngresos: 12000,
        categoria: "Panadería",
      },
      {
        id: "prod_11",
        nombre: "Cheesecake",
        cantidadVendida: 2,
        totalIngresos: 36000,
        categoria: "Postres",
      },
      {
        id: "prod_12",
        nombre: "Mix de Nueces",
        cantidadVendida: 1,
        totalIngresos: 9000,
        categoria: "Snacks",
      },
      {
        id: "prod_13",
        nombre: "Muffin de Arándanos",
        cantidadVendida: 1,
        totalIngresos: 7000,
        categoria: "Panadería",
      },
      {
        id: "prod_14",
        nombre: "Brownie",
        cantidadVendida: 1,
        totalIngresos: 7000,
        categoria: "Postres",
      },
      {
        id: "prod_15",
        nombre: "Macaron",
        cantidadVendida: 1,
        totalIngresos: 14000,
        categoria: "Postres",
      },
    ],
  },
  semana: {
    categories: [
      { name: "Bebidas", value: 93, color: "#3b82f6" },
      { name: "Panadería", value: 74, color: "#10B981" },
      { name: "Snacks", value: 48, color: "#8B5CF6" },
      { name: "Postres", value: 27, color: "#F59E0B" },
      { name: "Otros", value: 19, color: "#94A3B8" },
    ],
    topProducts: [
      {
        id: "prod_1",
        nombre: "Café Premium",
        cantidadVendida: 93,
        totalIngresos: 697500,
        categoria: "Bebidas",
      },
      {
        id: "prod_3",
        nombre: "Jugo Natural",
        cantidadVendida: 74,
        totalIngresos: 370000,
        categoria: "Bebidas",
      },
      {
        id: "prod_2",
        nombre: "Pan Artesanal",
        cantidadVendida: 62,
        totalIngresos: 310000,
        categoria: "Panadería",
      },
      {
        id: "prod_4",
        nombre: "Croissant",
        cantidadVendida: 48,
        totalIngresos: 240000,
        categoria: "Panadería",
      },
      {
        id: "prod_5",
        nombre: "Torta de Chocolate",
        cantidadVendida: 27,
        totalIngresos: 405000,
        categoria: "Postres",
      },
      {
        id: "prod_6",
        nombre: "Agua Mineral",
        cantidadVendida: 22,
        totalIngresos: 44000,
        categoria: "Bebidas",
      },
      {
        id: "prod_7",
        nombre: "Smoothie de Frutas",
        cantidadVendida: 18,
        totalIngresos: 162000,
        categoria: "Bebidas",
      },
      {
        id: "prod_8",
        nombre: "Chips de Maíz",
        cantidadVendida: 16,
        totalIngresos: 56000,
        categoria: "Snacks",
      },
      {
        id: "prod_9",
        nombre: "Baguette",
        cantidadVendida: 14,
        totalIngresos: 84000,
        categoria: "Panadería",
      },
      {
        id: "prod_10",
        nombre: "Cheesecake",
        cantidadVendida: 12,
        totalIngresos: 216000,
        categoria: "Postres",
      },
      {
        id: "prod_11",
        nombre: "Granola Bar",
        cantidadVendida: 10,
        totalIngresos: 60000,
        categoria: "Snacks",
      },
      {
        id: "prod_12",
        nombre: "Mix de Nueces",
        cantidadVendida: 8,
        totalIngresos: 72000,
        categoria: "Snacks",
      },
      {
        id: "prod_13",
        nombre: "Brownie",
        cantidadVendida: 6,
        totalIngresos: 42000,
        categoria: "Postres",
      },
      {
        id: "prod_14",
        nombre: "Muffin de Arándanos",
        cantidadVendida: 5,
        totalIngresos: 35000,
        categoria: "Panadería",
      },
      {
        id: "prod_15",
        nombre: "Macaron",
        cantidadVendida: 4,
        totalIngresos: 56000,
        categoria: "Postres",
      },
    ],
  },
  mes: {
    // ← Valores originales del mock (baseline de referencia del sistema)
    categories: [
      { name: "Bebidas", value: 52, color: "#3b82f6" },
      { name: "Panadería", value: 30, color: "#10B981" },
      { name: "Snacks", value: 16, color: "#8B5CF6" },
      { name: "Postres", value: 9, color: "#F59E0B" },
      { name: "Salados", value: 6, color: "#06B6D4" },
      { name: "Ensaladas", value: 4, color: "#EF4444" },
      { name: "Otros", value: 3, color: "#94A3B8" },
    ],
    topProducts: [
      {
        id: "prod_1",
        nombre: "Café Premium",
        cantidadVendida: 45,
        totalIngresos: 337500,
        categoria: "Bebidas",
      },
      {
        id: "prod_2",
        nombre: "Pan Artesanal",
        cantidadVendida: 38,
        totalIngresos: 190000,
        categoria: "Panadería",
      },
      {
        id: "prod_3",
        nombre: "Jugo Natural",
        cantidadVendida: 30,
        totalIngresos: 150000,
        categoria: "Bebidas",
      },
      {
        id: "prod_4",
        nombre: "Croissant",
        cantidadVendida: 25,
        totalIngresos: 125000,
        categoria: "Panadería",
      },
      {
        id: "prod_5",
        nombre: "Torta de Chocolate",
        cantidadVendida: 12,
        totalIngresos: 180000,
        categoria: "Postres",
      },
      {
        id: "prod_6",
        nombre: "Agua Mineral",
        cantidadVendida: 10,
        totalIngresos: 20000,
        categoria: "Bebidas",
      },
      {
        id: "prod_7",
        nombre: "Chips de Maíz",
        cantidadVendida: 9,
        totalIngresos: 31500,
        categoria: "Snacks",
      },
      {
        id: "prod_8",
        nombre: "Smoothie de Frutas",
        cantidadVendida: 8,
        totalIngresos: 72000,
        categoria: "Bebidas",
      },
      {
        id: "prod_9",
        nombre: "Cheesecake",
        cantidadVendida: 7,
        totalIngresos: 126000,
        categoria: "Postres",
      },
      {
        id: "prod_10",
        nombre: "Granola Bar",
        cantidadVendida: 7,
        totalIngresos: 42000,
        categoria: "Snacks",
      },
      {
        id: "prod_11",
        nombre: "Baguette",
        cantidadVendida: 6,
        totalIngresos: 36000,
        categoria: "Panadería",
      },
      {
        id: "prod_12",
        nombre: "Mix de Nueces",
        cantidadVendida: 5,
        totalIngresos: 45000,
        categoria: "Snacks",
      },
      {
        id: "prod_13",
        nombre: "Muffin de Arándanos",
        cantidadVendida: 4,
        totalIngresos: 28000,
        categoria: "Panadería",
      },
      {
        id: "prod_14",
        nombre: "Brownie",
        cantidadVendida: 3,
        totalIngresos: 21000,
        categoria: "Postres",
      },
      {
        id: "prod_15",
        nombre: "Macaron",
        cantidadVendida: 3,
        totalIngresos: 42000,
        categoria: "Postres",
      },
    ],
  },
  anio: {
    categories: [
      { name: "Bebidas", value: 540, color: "#3b82f6" },
      { name: "Panadería", value: 420, color: "#10B981" },
      { name: "Snacks", value: 290, color: "#8B5CF6" },
      { name: "Postres", value: 145, color: "#F59E0B" },
      { name: "Otros", value: 95, color: "#94A3B8" },
    ],
    topProducts: [
      {
        id: "prod_1",
        nombre: "Café Premium",
        cantidadVendida: 540,
        totalIngresos: 4050000,
        categoria: "Bebidas",
      },
      {
        id: "prod_3",
        nombre: "Jugo Natural",
        cantidadVendida: 420,
        totalIngresos: 2100000,
        categoria: "Bebidas",
      },
      {
        id: "prod_2",
        nombre: "Pan Artesanal",
        cantidadVendida: 380,
        totalIngresos: 1900000,
        categoria: "Panadería",
      },
      {
        id: "prod_5",
        nombre: "Torta de Chocolate",
        cantidadVendida: 290,
        totalIngresos: 4350000,
        categoria: "Postres",
      },
      {
        id: "prod_4",
        nombre: "Croissant",
        cantidadVendida: 250,
        totalIngresos: 1250000,
        categoria: "Panadería",
      },
      {
        id: "prod_6",
        nombre: "Agua Mineral",
        cantidadVendida: 180,
        totalIngresos: 360000,
        categoria: "Bebidas",
      },
      {
        id: "prod_7",
        nombre: "Chips de Maíz",
        cantidadVendida: 145,
        totalIngresos: 507500,
        categoria: "Snacks",
      },
      {
        id: "prod_8",
        nombre: "Smoothie de Frutas",
        cantidadVendida: 120,
        totalIngresos: 1080000,
        categoria: "Bebidas",
      },
      {
        id: "prod_9",
        nombre: "Granola Bar",
        cantidadVendida: 95,
        totalIngresos: 570000,
        categoria: "Snacks",
      },
      {
        id: "prod_10",
        nombre: "Cheesecake",
        cantidadVendida: 82,
        totalIngresos: 1476000,
        categoria: "Postres",
      },
      {
        id: "prod_11",
        nombre: "Baguette",
        cantidadVendida: 75,
        totalIngresos: 450000,
        categoria: "Panadería",
      },
      {
        id: "prod_12",
        nombre: "Mix de Nueces",
        cantidadVendida: 60,
        totalIngresos: 540000,
        categoria: "Snacks",
      },
      {
        id: "prod_13",
        nombre: "Muffin de Arándanos",
        cantidadVendida: 45,
        totalIngresos: 315000,
        categoria: "Panadería",
      },
      {
        id: "prod_14",
        nombre: "Brownie",
        cantidadVendida: 38,
        totalIngresos: 266000,
        categoria: "Postres",
      },
      {
        id: "prod_15",
        nombre: "Macaron",
        cantidadVendida: 28,
        totalIngresos: 392000,
        categoria: "Postres",
      },
    ],
  },
};

/** Opciones de periodo para el menú del donut — etiquetas en español */
export const DONUT_PERIOD_OPTIONS: Array<{
  value: DonutPeriod;
  label: string;
}> = [
  { value: "hoy", label: "Hoy" },
  { value: "semana", label: "Semana" },
  { value: "mes", label: "Mes" },
  { value: "anio", label: "Año" },
];

/* ─────────────── Generadores para drill-down temporal ─────────────── */

/**
 * Generador pseudo-aleatorio determinístico — LCG (Linear Congruential Generator).
 * No usa Math.sin para evitar pérdida de precisión con semillas grandes.
 * Produce siempre el mismo float [0,1) para la misma semilla.
 */
function seededRandom(seed: number): number {
  const a = 1664525;
  const c = 1013904223;
  const m = 4294967296; // 2^32
  return ((Math.abs(Math.round(seed)) * a + c) % m) / m;
}

/** Proporciones base por categoría — usadas para generar variaciones */
const BASE_CATEGORIES = [
  { name: "Bebidas", basePct: 0.28, color: "#3b82f6" },
  { name: "Panadería", basePct: 0.24, color: "#10B981" },
  { name: "Snacks", basePct: 0.22, color: "#8B5CF6" },
  { name: "Postres", basePct: 0.15, color: "#F59E0B" },
  { name: "Otros", basePct: 0.11, color: "#94A3B8" },
] as const;

/**
 * Total de unidades para un periodo — mock determinístico.
 * Usado para mostrar la cifra en los tiles del picker de fechas.
 */
export function getTileTotal(
  date: Date,
  level: "decada" | "anio" | "mes" | "dia",
): number {
  const seed =
    date.getFullYear() * 10000 + (date.getMonth() + 1) * 100 + date.getDate();
  const bases: Record<string, number> = {
    decada: 14200,
    anio: 1490,
    mes: 128,
    dia: 50,
  };
  return Math.round(bases[level] * (0.75 + seededRandom(seed) * 0.5));
}

/**
 * Genera datos de categorías y top-productos para una fecha y nivel específicos.
 * Produce distribuciones levemente distintas para cada combinación fecha+nivel,
 * manteniendo las proporciones base como punto de referencia.
 */
export function generateCategoryDataForDate(
  date: Date,
  level: "hora" | "hoy" | "mes" | "anio",
): { categories: ChartSlice[]; topProducts: TopProduct[] } {
  const h = level === "hora" ? date.getHours() : 0;
  // Semilla compacta y única por año/mes/día/hora — evita desbordamiento de precisión
  const seed =
    date.getFullYear() * 8784 + date.getMonth() * 744 + date.getDate() * 24 + h;
  // "hora": ~20 unidades/hora → variación real entre categorías (4–7 por slice)
  const multipliers: Record<string, number> = {
    hora: 20,
    hoy: 50,
    mes: 128,
    anio: 1490,
  };
  const mult = multipliers[level];

  const categories: ChartSlice[] = BASE_CATEGORIES.map((cat, i) => {
    const variation = 0.75 + seededRandom(seed + i * 13 + 7) * 0.5;
    const value = Math.max(1, Math.round(cat.basePct * mult * variation));
    return { name: cat.name, value, color: cat.color };
  });

  const topProducts: TopProduct[] = [
    {
      id: "prod_1",
      nombre: "Café Premium",
      cantidadVendida: categories[0].value,
      totalIngresos: categories[0].value * 7500,
      categoria: "Bebidas",
    },
    {
      id: "prod_2",
      nombre: "Pan Artesanal",
      cantidadVendida: categories[1].value,
      totalIngresos: categories[1].value * 5000,
      categoria: "Panadería",
    },
    {
      id: "prod_3",
      nombre: "Jugo Natural",
      cantidadVendida: Math.round(categories[0].value * 0.65),
      totalIngresos: Math.round(categories[0].value * 0.65) * 4800,
      categoria: "Bebidas",
    },
    {
      id: "prod_4",
      nombre: "Croissant",
      cantidadVendida: categories[2].value,
      totalIngresos: categories[2].value * 5000,
      categoria: "Panadería",
    },
    {
      id: "prod_5",
      nombre: "Torta de Chocolate",
      cantidadVendida: categories[3].value,
      totalIngresos: categories[3].value * 15000,
      categoria: "Postres",
    },
    {
      id: "prod_6",
      nombre: "Agua Mineral",
      cantidadVendida: Math.max(1, Math.round(categories[0].value * 0.32)),
      totalIngresos: Math.max(1, Math.round(categories[0].value * 0.32)) * 2000,
      categoria: "Bebidas",
    },
    {
      id: "prod_7",
      nombre: "Smoothie de Frutas",
      cantidadVendida: Math.max(1, Math.round(categories[0].value * 0.24)),
      totalIngresos: Math.max(1, Math.round(categories[0].value * 0.24)) * 9000,
      categoria: "Bebidas",
    },
    {
      id: "prod_8",
      nombre: "Chips de Maíz",
      cantidadVendida: Math.max(1, Math.round(categories[2].value * 0.48)),
      totalIngresos: Math.max(1, Math.round(categories[2].value * 0.48)) * 3500,
      categoria: "Snacks",
    },
    {
      id: "prod_9",
      nombre: "Baguette",
      cantidadVendida: Math.max(1, Math.round(categories[1].value * 0.32)),
      totalIngresos: Math.max(1, Math.round(categories[1].value * 0.32)) * 6000,
      categoria: "Panadería",
    },
    {
      id: "prod_10",
      nombre: "Cheesecake",
      cantidadVendida: Math.max(1, Math.round(categories[3].value * 0.52)),
      totalIngresos:
        Math.max(1, Math.round(categories[3].value * 0.52)) * 18000,
      categoria: "Postres",
    },
    {
      id: "prod_11",
      nombre: "Granola Bar",
      cantidadVendida: Math.max(1, Math.round(categories[2].value * 0.38)),
      totalIngresos: Math.max(1, Math.round(categories[2].value * 0.38)) * 6000,
      categoria: "Snacks",
    },
    {
      id: "prod_12",
      nombre: "Mix de Nueces",
      cantidadVendida: Math.max(1, Math.round(categories[2].value * 0.26)),
      totalIngresos: Math.max(1, Math.round(categories[2].value * 0.26)) * 9000,
      categoria: "Snacks",
    },
    {
      id: "prod_13",
      nombre: "Muffin de Arándanos",
      cantidadVendida: Math.max(1, Math.round(categories[1].value * 0.18)),
      totalIngresos: Math.max(1, Math.round(categories[1].value * 0.18)) * 7000,
      categoria: "Panadería",
    },
    {
      id: "prod_14",
      nombre: "Brownie",
      cantidadVendida: Math.max(1, Math.round(categories[3].value * 0.3)),
      totalIngresos: Math.max(1, Math.round(categories[3].value * 0.3)) * 7000,
      categoria: "Postres",
    },
    {
      id: "prod_15",
      nombre: "Macaron",
      cantidadVendida: Math.max(1, Math.round(categories[3].value * 0.15)),
      totalIngresos:
        Math.max(1, Math.round(categories[3].value * 0.15)) * 14000,
      categoria: "Postres",
    },
  ].sort((a, b) => b.cantidadVendida - a.cantidadVendida);

  return { categories, topProducts };
}
